import React, { useState, useEffect } from 'react';
import apiService from '../apiService';

const SCOPES = [
  { key: 'taxi', label: 'Courses taxi' },
  { key: 'delivery', label: 'Livraisons' },
  { key: 'parcel', label: 'Colis' },
];

const emptyForm = () => ({
  name: '', enabled: true,
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  threshold: 100, reward_amount: 50000,
  scope: ['taxi', 'delivery', 'parcel'], city_slug: '',
});

export default function DriverPromosPage() {
  const [promos, setPromos] = useState([]);
  const [awards, setAwards] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        apiService.get('/admin/driver-promos', { userType: 'admin' }),
        apiService.get('/admin/driver-promos/awards/list', { userType: 'admin' }),
      ]);
      if (p.success) setPromos(p.promos || []);
      if (a.success) { setAwards(a.awards || []); setTotalPaid(a.total_paid || 0); }
    } catch (e) { setMsg({ type: 'error', text: 'Erreur de chargement' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3000); };

  const toggleScope = (k) => setForm(f => ({
    ...f, scope: f.scope.includes(k) ? f.scope.filter(s => s !== k) : [...f.scope, k],
  }));

  const submit = async () => {
    if (!form.name.trim()) return flash('error', 'Nom requis');
    if (!form.scope.length) return flash('error', 'Choisissez au moins un périmètre');
    const payload = { ...form, city_slug: form.city_slug.trim() || null };
    try {
      if (editingId) await apiService.put(`/admin/driver-promos/${editingId}`, payload, { userType: 'admin' });
      else await apiService.post('/admin/driver-promos', payload, { userType: 'admin' });
      setForm(emptyForm()); setEditingId(null);
      flash('success', editingId ? 'Promotion modifiée' : 'Promotion créée');
      load();
    } catch (e) { flash('error', e.response?.data?.error || 'Erreur'); }
  };

  const startEdit = (p) => {
    setEditingId(p.promo_id);
    setForm({
      name: p.name, enabled: p.enabled,
      start_date: p.start_date?.slice(0, 10), end_date: p.end_date?.slice(0, 10),
      threshold: p.threshold, reward_amount: p.reward_amount,
      scope: p.scope || [], city_slug: p.city_slug || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleEnabled = async (p) => {
    try { await apiService.put(`/admin/driver-promos/${p.promo_id}`, { enabled: !p.enabled }, { userType: 'admin' }); load(); }
    catch { flash('error', 'Erreur'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer la promotion « ${p.name} » ?`)) return;
    try { await apiService.delete(`/admin/driver-promos/${p.promo_id}`, { userType: 'admin' }); load(); flash('success', 'Supprimée'); }
    catch { flash('error', 'Erreur'); }
  };

  const runNow = async () => {
    if (!window.confirm('Forcer l\'évaluation de la semaine écoulée maintenant ? (les bonus atteints seront versés)')) return;
    try { const r = await apiService.post('/admin/driver-promos/run-now', {}, { userType: 'admin' }); flash('success', `Évalué : ${r.awarded || 0} bonus versé(s)`); load(); }
    catch { flash('error', 'Erreur'); }
  };

  const card = { background: 'white', borderRadius: 16, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };
  const inputRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: '1px solid #F4F5F7' };
  const input = { width: 200, padding: '8px 12px', border: '1px solid #DCE0E6', borderRadius: 8, fontSize: '0.95rem' };
  const btn = { padding: '10px 20px', background: '#F54119', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' };

  if (loading) return <div className="dashboard-content"><p>Chargement…</p></div>;

  return (
    <div className="dashboard-content">
      <h1 className="page-title">🎁 Promotions chauffeurs</h1>
      {msg.text && (
        <div style={{ padding: '12px 16px', borderRadius: 10, marginBottom: '1rem', background: msg.type === 'success' ? '#ecfdf5' : '#fef2f2', color: msg.type === 'success' ? '#059669' : '#dc2626', fontWeight: 600 }}>{msg.text}</div>
      )}

      {/* Formulaire création / édition */}
      <div style={card}>
        <h3 style={{ margin: '0 0 1rem' }}>{editingId ? '✏️ Modifier la promotion' : '➕ Nouvelle promotion'}</h3>
        <div style={inputRow}><span>Nom</span><input style={{ ...input, width: 260 }} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex. Défi 100 courses" /></div>
        <div style={inputRow}><span>Activée</span><input type="checkbox" style={{ width: 20, height: 20 }} checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} /></div>
        <div style={inputRow}><span>Date de début</span><input type="date" style={input} value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
        <div style={inputRow}><span>Date de fin</span><input type="date" style={input} value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
        <div style={inputRow}><span>Seuil (nb de courses / semaine)</span><input type="number" style={input} value={form.threshold} onChange={e => setForm({ ...form, threshold: parseInt(e.target.value) || 0 })} /></div>
        <div style={inputRow}><span>Récompense (CDF)</span><input type="number" style={input} value={form.reward_amount} onChange={e => setForm({ ...form, reward_amount: parseInt(e.target.value) || 0 })} /></div>
        <div style={inputRow}>
          <span>Périmètre (ce qui compte)</span>
          <div style={{ display: 'flex', gap: 14 }}>
            {SCOPES.map(s => (
              <label key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.9rem' }}>
                <input type="checkbox" checked={form.scope.includes(s.key)} onChange={() => toggleScope(s.key)} /> {s.label}
              </label>
            ))}
          </div>
        </div>
        <div style={inputRow}><span>Ville (slug — vide = toutes)</span><input style={input} value={form.city_slug} onChange={e => setForm({ ...form, city_slug: e.target.value })} placeholder="kinshasa (optionnel)" /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: '1rem' }}>
          <button style={btn} onClick={submit}>{editingId ? 'Enregistrer' : 'Créer la promotion'}</button>
          {editingId && <button style={{ ...btn, background: '#9AA0A8' }} onClick={() => { setEditingId(null); setForm(emptyForm()); }}>Annuler</button>}
        </div>
      </div>

      {/* Liste des promotions */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Promotions ({promos.length})</h3>
          <button style={{ ...btn, background: '#059669' }} onClick={runNow}>Évaluer la semaine écoulée</button>
        </div>
        {promos.length === 0 ? <p style={{ color: '#5C636E' }}>Aucune promotion.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead><tr style={{ textAlign: 'left', color: '#5C636E', borderBottom: '2px solid #E9EBEF' }}>
                <th style={{ padding: 8 }}>Nom</th><th>Statut</th><th>Seuil</th><th>Récompense</th><th>Périmètre</th><th>Ville</th><th>Période</th><th></th>
              </tr></thead>
              <tbody>
                {promos.map(p => (
                  <tr key={p.promo_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                    <td style={{ padding: 8, fontWeight: 600 }}>{p.name}</td>
                    <td><span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 700, background: p.enabled ? '#ecfdf5' : '#F4F5F7', color: p.enabled ? '#059669' : '#5C636E' }}>{p.enabled ? 'Active' : 'Inactive'}</span></td>
                    <td>{p.threshold}</td>
                    <td>{(p.reward_amount || 0).toLocaleString('fr-FR')} CDF</td>
                    <td>{(p.scope || []).join(', ')}</td>
                    <td>{p.city_slug || 'Toutes'}</td>
                    <td>{p.start_date?.slice(0, 10)} → {p.end_date?.slice(0, 10)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button style={{ ...btn, padding: '5px 10px', background: '#5C636E', marginRight: 6 }} onClick={() => toggleEnabled(p)}>{p.enabled ? 'Désactiver' : 'Activer'}</button>
                      <button style={{ ...btn, padding: '5px 10px', marginRight: 6 }} onClick={() => startEdit(p)}>Modifier</button>
                      <button style={{ ...btn, padding: '5px 10px', background: '#dc2626' }} onClick={() => remove(p)}>Suppr.</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bonus versés */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.5rem' }}>Bonus versés — total {totalPaid.toLocaleString('fr-FR')} CDF ({awards.length})</h3>
        {awards.length === 0 ? <p style={{ color: '#5C636E' }}>Aucun bonus versé pour l'instant.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead><tr style={{ textAlign: 'left', color: '#5C636E', borderBottom: '2px solid #E9EBEF' }}>
                <th style={{ padding: 8 }}>Date</th><th>Chauffeur</th><th>Semaine</th><th>Courses</th><th>Montant</th>
              </tr></thead>
              <tbody>
                {awards.map(a => (
                  <tr key={a.award_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                    <td style={{ padding: 8 }}>{new Date(a.awarded_at).toLocaleDateString('fr-FR')}</td>
                    <td>{a.driver_id}</td>
                    <td>{a.period_key}</td>
                    <td>{a.rides_count}</td>
                    <td>{(a.amount || 0).toLocaleString('fr-FR')} CDF</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
